import { Link, useParams } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Transaction } from "@/apis/gql/types";
import {
  AccountingTransactionsReadyToExportBadge,
  AccountingTransactionsTodosBadge,
} from "@/components/shared/app-navigation/sidebar/badges/company";
import AIProcessingCard from "@/components/shared/cards/ai-processing-card";
import { UploadInvoicesDrawer } from "@/components/shared/drawers/upload-invoices-drawer";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import { AllTransactions } from "@/features/transactions/shared/all-transactions";
import UploadProgressBox from "@/features/upload-progress-box/UploadProgressBox";
import { useArrayQueryState } from "@/hooks/useArrayQueryState";
import { cn } from "@/lib/utils";
import { useAddInvoiceToTransaction } from "@/queries/transactions/add-invoice";
import { useApproveTransactionBooking } from "@/queries/transactions/approve-transaction-booking";
import { useDeleteInvoiceFromTransaction } from "@/queries/transactions/delete-invoice";

import { useAllTransactionsTable } from "../shared/use-all-transactions-table";

import { columns } from "./all-transactions-columns";
import { TransactionsTableFilters } from "./all-transactions-table-filters";
import { BookingPanel, BookingPanelProvider } from "./booking-panel";

export default function Transactions() {
  const { t } = useTranslation();
  const { company: companyId } = useParams({ strict: false });

  const [approveTransactionBooking] = useApproveTransactionBooking();
  const [addInvoiceToTransaction] = useAddInvoiceToTransaction();
  const [deleteInvoiceFromTransaction] = useDeleteInvoiceFromTransaction();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedTransactionIds, setSelectedTransactionIds] =
    useArrayQueryState("selected-transaction-ids");
  const [selectedInvoicesIds, setSelectedInvoicesIds] = useState<
    Record<string, boolean>
  >({});

  const allTransactions = useAllTransactionsTable({
    columns,
    rowSelection: selectedTransactionIds,
    onRowSelectionChange: setSelectedTransactionIds,
  });

  const { globalSearch, setGlobalSearch, filters, setFilters } =
    allTransactions;

  const openedTransactionOnAllTransactions: Transaction[] =
    allTransactions.table.getFilteredSelectedRowModel().rows.length === 1
      ? allTransactions.table
          .getFilteredSelectedRowModel()
          .rows.map((row) => row.original)
      : [];

  const isPanelOpen = openedTransactionOnAllTransactions.length > 0;

  useEffect(() => {
    // Small delay to let the DOM update, then trigger resize
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 100);
    return () => clearTimeout(timer);
  }, [isPanelOpen]);

  if (!companyId) return null;

  return (
    <BookingPanelProvider selectedTransactionIds={selectedTransactionIds}>
      <div className="h-full flex">
        <div className="flex flex-1 h-full">
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex px-4 bg-background pb-2">
              <AIProcessingCard screen="txn-todos" className="mt-4" />
            </div>
            <div className="flex-1 flex">
              <div className="flex flex-col min-w-0 w-full relative">
                <div>
                  <NavigationMenu className="px-4 bg-background max-w-full justify-start border-b">
                    <NavigationMenuList className="space-x-0">
                      <NavigationMenuItem>
                        <Link
                          to="/$company/buchhaltung/transaktionen/todos"
                          params={{ company: companyId }}
                        >
                          <NavigationMenuLink
                            active
                            className={cn(
                              "flex items-center py-3 px-6 gap-4 font-medium text-lg",
                              "inline-flex justify-center whitespace-nowrap ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                              "text-gray-500",
                            )}
                          >
                            {t("transactions.tabs.todos")}
                            <AccountingTransactionsTodosBadge />
                          </NavigationMenuLink>
                        </Link>
                      </NavigationMenuItem>
                      <NavigationMenuItem>
                        <Link
                          to="/$company/buchhaltung/transaktionen/export"
                          params={{ company: companyId }}
                        >
                          <NavigationMenuLink
                            className={cn(
                              "flex items-center py-3 px-6 gap-4 font-medium text-lg",
                              "inline-flex justify-center whitespace-nowrap ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                              "text-gray-500",
                            )}
                          >
                            {t("transactions.tabs.export")}
                            <AccountingTransactionsReadyToExportBadge />
                          </NavigationMenuLink>
                        </Link>
                      </NavigationMenuItem>
                      <NavigationMenuItem>
                        <Link
                          to="/$company/buchhaltung/transaktionen"
                          params={{ company: companyId }}
                        >
                          <NavigationMenuLink
                            active
                            className={cn(
                              "flex items-center py-3 px-6 gap-2 font-medium text-lg rounded-none",
                              "inline-flex justify-center whitespace-nowrap ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                              "border-b-[3px] text-primary border-primary cursor-default",
                            )}
                            onClick={(e) => e.preventDefault()}
                          >
                            {t("transactions.tabs.all_transactions")}
                          </NavigationMenuLink>
                        </Link>
                      </NavigationMenuItem>
                    </NavigationMenuList>
                  </NavigationMenu>
                </div>
                <div className="flex-1 flex flex-col">
                  <div className="flex-1 px-4 pb-4">
                    <AllTransactions
                      {...allTransactions}
                      selectedInvoicesIds={selectedInvoicesIds}
                      handleMatch={handleMatch}
                    >
                      <AllTransactions.Filters>
                        <TransactionsTableFilters
                          globalSearch={globalSearch}
                          setGlobalSearch={setGlobalSearch}
                          filters={filters}
                          setFilters={setFilters}
                        />
                      </AllTransactions.Filters>
                    </AllTransactions>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <AnimatePresence mode="wait">
            {openedTransactionOnAllTransactions.length > 0 && (
              <motion.div
                key="booking-panel-wrapper"
                className="bg-background z-10 overflow-hidden"
                style={{
                  width: "50%",
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.3,
                  ease: [0.4, 0, 0.2, 1],
                }}
              >
                <BookingPanel
                  key={openedTransactionOnAllTransactions
                    .map((t) => t.bankTxnId)
                    .join(",")}
                  transactions={openedTransactionOnAllTransactions}
                  onRowSelectionChange={setSelectedInvoicesIds}
                  rowSelection={selectedInvoicesIds}
                  onMatch={handleMatch}
                  onUnmatch={handleUnmatch}
                  onClose={handleClosePanel}
                  onConfirmRawBookings={handleConfirmBookings}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <UploadInvoicesDrawer isOpen={isOpen} setIsOpen={setIsOpen} />
      <UploadProgressBox />
    </BookingPanelProvider>
  );

  async function handleMatch(invoiceIds?: string[]) {
    const relevantInvoiceIds = invoiceIds ?? Object.keys(selectedInvoicesIds);

    const bankTxnIds = openedTransactionOnAllTransactions.map(
      (t) => t.bankTxnId,
    );

    await addInvoiceToTransaction({
      bankTxnIds,
      invoiceIds: relevantInvoiceIds,
    });

    setSelectedInvoicesIds({});
  }

  async function handleUnmatch(invoiceIds?: string[]) {
    const relevantInvoiceIds = invoiceIds ?? Object.keys(selectedInvoicesIds);

    const bankTxnIds = openedTransactionOnAllTransactions.map(
      (t) => t.bankTxnId,
    );

    await deleteInvoiceFromTransaction({
      bankTxnIds,
      invoiceIds: relevantInvoiceIds,
    });
  }

  async function handleConfirmBookings(bankTxnIds?: string[]) {
    if (!bankTxnIds) return;

    await Promise.all(
      bankTxnIds.map(async (transactionId) => {
        return approveTransactionBooking({
          bankTransactionId: transactionId,
        });
      }),
    );
  }

  async function handleClosePanel() {
    setSelectedTransactionIds({});
    setSelectedInvoicesIds({});
  }
}
